import React, { useEffect, useState } from 'react';
import api from '../api';
import { useTranslation } from '../LanguageContext';

const formatDate = (value) => value ? new Date(value).toLocaleString('en-GB') : '—';

const usd = (value) => `$${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

/**
 * Arbitration desk. The ruling is made against the campaign brief and the
 * evidence thread, then the escrow is split accordingly.
 */
export function AdminDisputes() {
    const { t } = useTranslation();
    const [disputes, setDisputes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openId, setOpenId] = useState(null);

    const load = async () => {
        try {
            const res = await api.get('/api/admin/disputes/');
            setDisputes(res.data || []);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    if (loading) return <div className="flex justify-center py-10"><div className="spinner" /></div>;
    if (!disputes.length) {
        return <p className="text-sm text-slate-400 text-center py-10">{t('admin.noDisputes')}</p>;
    }

    return (
        <div className="space-y-3">
            {disputes.map(dispute => (
                <DisputeRow key={dispute.id} dispute={dispute} open={openId === dispute.id}
                    onToggle={() => setOpenId(openId === dispute.id ? null : dispute.id)}
                    onResolved={load} t={t} />
            ))}
        </div>
    );
}

function DisputeRow({ dispute, open, onToggle, onResolved, t }) {
    const [share, setShare] = useState(50);
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [reply, setReply] = useState('');
    const deal = dispute.deal;
    const campaign = dispute.campaign;
    const resolved = dispute.status === 'resolved';

    const resolve = async (resolution) => {
        const label = resolution === 'split'
            ? `${share}% / ${100 - share}%`
            : t(`campaigns.resolution_${resolution}`);
        if (!window.confirm(`${t('admin.confirmRuling')}\n\n${label}`)) return;
        setBusy(true);
        try {
            await api.post('/api/admin/disputes/resolve/', {
                dispute_id: dispute.id, resolution, creator_share: share, admin_note: note,
            });
            onResolved();
        } finally { setBusy(false); }
    };

    const sendReply = async () => {
        if (!reply.trim()) return;
        setBusy(true);
        try {
            await api.post(`/api/campaigns/disputes/${dispute.id}/messages/`, { text: reply });
            setReply('');
            onResolved();
        } finally { setBusy(false); }
    };

    return (
        <div className={`rounded-2xl border bg-white overflow-hidden ${resolved ? 'border-slate-200 opacity-75' : 'border-red-200'}`}>
            <button onClick={onToggle} className="w-full text-left p-4 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-800 truncate">{campaign?.title}</p>
                    <p className="text-[11px] text-slate-400">
                        {deal?.brand?.username} ↔ {deal?.creator?.username} · {formatDate(dispute.created_at)}
                    </p>
                </div>
                <div className="text-right flex-shrink-0">
                    <p className="text-sm font-black text-slate-800 tabular-nums">{usd(deal?.amount_usd)}</p>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${resolved
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-red-50 text-red-600 border-red-200'}`}>
                        {resolved ? t('admin.disputeResolved') : t('admin.disputeOpen')}
                    </span>
                </div>
            </button>

            {open && (
                <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
                    {/* The brief the ruling is measured against */}
                    <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                        <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2">
                            {t('admin.theBrief')}
                        </p>
                        {campaign?.summary && <p className="text-xs text-slate-600 mb-2">{campaign.summary}</p>}
                        {(campaign?.sections || []).map(section => (
                            <div key={section.id} className="mb-2">
                                <p className="text-xs font-bold text-slate-800">{section.title}</p>
                                <p className="text-xs text-slate-600 whitespace-pre-line">{section.body}</p>
                            </div>
                        ))}
                        {(campaign?.deliverables || []).length > 0 && (
                            <ul className="mt-1">
                                {campaign.deliverables.map((row, index) => (
                                    <li key={index} className="text-xs text-slate-600">
                                        • {row.quantity || 1}× {row.format} — {row.platform}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Deliveries + revision history */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="rounded-xl border border-slate-200 p-3">
                            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2">
                                {t('admin.deliveries')}
                            </p>
                            {(deal?.deliveries || []).map(delivery => (
                                <div key={delivery.id} className="mb-2">
                                    <p className="text-[10px] text-slate-400">#{delivery.round} · {formatDate(delivery.created_at)}</p>
                                    {delivery.link && (
                                        <a href={delivery.link} target="_blank" rel="noopener noreferrer"
                                            className="text-xs text-primary-700 hover:underline break-all">{delivery.link}</a>
                                    )}
                                    {delivery.note && <p className="text-xs text-slate-600">{delivery.note}</p>}
                                </div>
                            ))}
                        </div>
                        <div className="rounded-xl border border-slate-200 p-3">
                            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2">
                                {t('admin.revisions')}
                            </p>
                            {(deal?.revisions || []).map(revision => (
                                <div key={revision.id} className="mb-2">
                                    <p className="text-[10px] text-slate-400">{formatDate(revision.created_at)}</p>
                                    {(revision.reasons || []).map(reason => (
                                        <p key={reason} className="text-xs text-slate-600">• {t(`campaigns.reason_${reason}`)}</p>
                                    ))}
                                    {revision.note && <p className="text-xs text-slate-600 italic">"{revision.note}"</p>}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Evidence thread */}
                    <div className="rounded-xl border border-slate-200 p-3">
                        <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2">
                            {t('campaigns.evidenceTitle')}
                        </p>
                        <div className="space-y-2 max-h-60 overflow-y-auto custom-scrollbar">
                            {(dispute.messages || []).map(message => (
                                <div key={message.id} className={`rounded-lg p-2 ${message.is_admin ? 'bg-violet-50' : 'bg-slate-50'}`}>
                                    <p className="text-[10px] font-bold text-slate-500">
                                        {message.is_admin ? t('campaigns.platformTeam') : message.sender?.username} · {formatDate(message.created_at)}
                                    </p>
                                    <p className="text-xs text-slate-700">{message.text}</p>
                                    {message.attachments?.length > 0 && (
                                        <div className="flex gap-1.5 mt-1">
                                            {message.attachments.map((file, index) => (
                                                <a key={index} href={file.url} target="_blank" rel="noopener noreferrer">
                                                    <img src={file.url} alt="" className="w-12 h-12 rounded object-cover" />
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        {!resolved && (
                            <div className="flex gap-2 mt-2">
                                <input className="input py-1.5 text-xs" value={reply}
                                    placeholder={t('admin.replyPlaceholder')}
                                    onChange={(e) => setReply(e.target.value)} />
                                <button onClick={sendReply} disabled={busy} className="btn-secondary px-3 py-1.5 text-xs">
                                    {t('campaigns.send')}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Ruling */}
                    {resolved ? (
                        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3">
                            <p className="text-xs font-bold text-emerald-800">
                                {t(`campaigns.resolution_${dispute.resolution}`)}
                                {dispute.resolution === 'split' && ` (${dispute.creator_share}% / ${100 - dispute.creator_share}%)`}
                            </p>
                            <p className="text-[11px] text-emerald-700 mt-0.5 tabular-nums">
                                {t('campaigns.creatorGot')} {usd(deal?.creator_usd)} · {t('campaigns.brandGot')} {usd(deal?.brand_usd)}
                            </p>
                            {dispute.admin_note && <p className="text-[11px] text-slate-500 italic mt-1">"{dispute.admin_note}"</p>}
                        </div>
                    ) : (
                        <div className="rounded-xl border border-slate-200 p-3 space-y-2">
                            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">
                                {t('admin.ruling')}
                            </p>
                            <input className="input py-2 text-xs" value={note}
                                placeholder={t('admin.rulingNotePlaceholder')}
                                onChange={(e) => setNote(e.target.value)} />
                            <div className="flex items-center gap-2">
                                <label className="text-[11px] text-slate-500 flex-shrink-0">{t('admin.creatorShare')}</label>
                                <input type="range" min={0} max={100} step={5} value={share}
                                    onChange={(e) => setShare(Number(e.target.value))} className="flex-1 accent-[#20c9b5]" />
                                <span className="text-xs font-bold text-slate-700 w-20 text-right tabular-nums">
                                    {share}% / {100 - share}%
                                </span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <button onClick={() => resolve('creator')} disabled={busy}
                                    className="btn-success flex-1 py-2 text-xs min-w-[120px]">
                                    {t('admin.awardCreator')}
                                </button>
                                <button onClick={() => resolve('brand')} disabled={busy}
                                    className="btn-danger flex-1 py-2 text-xs min-w-[120px]">
                                    {t('admin.refundBrand')}
                                </button>
                                <button onClick={() => resolve('split')} disabled={busy}
                                    className="btn-secondary flex-1 py-2 text-xs min-w-[120px]">
                                    {t('admin.splitAmount')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
